// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { SectionTitleStyles } from "@guestsupply/App/Widgets/SectionTitle/SectionTitle";
import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import { customXngageWidget, fontFamilyValues } from "@guestsupply/XDL/constants";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, VisuallyHidden } from "@insite/mobius/index";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";

interface BenefitModel {
    fields: {
        heading: string;
        subHeading: string;
        benefitLink: LinkFieldValue;
        icon: string;
    };
}

const enum fields {
    heading = "heading",
    subHeading = "subHeading",
    firstButtonVariant = "firstButtonVariant",
    firstButtonLink = "firstButtonLink",
    firstButtonLabel = "firstButtonLabel",
    secondButtonVariant = "secondButtonVariant",
    secondButtonLink = "secondButtonLink",
    secondButtonLabel = "secondButtonLabel",
    openInNewTab1 = "openInNewTab1",
    openInNewTab2 = "openInNewTab2",
    showForAuthenticated = "showForAuthenticated",
    benefits = "benefits",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subHeading]: string;
        [fields.firstButtonLabel]: string;
        [fields.firstButtonLink]: LinkFieldValue;
        [fields.firstButtonVariant]: "primary" | "secondary" | "tertiary";
        [fields.openInNewTab1]: boolean;
        [fields.secondButtonLabel]: string;
        [fields.secondButtonLink]: LinkFieldValue;
        [fields.secondButtonVariant]: "primary" | "secondary" | "tertiary";
        [fields.openInNewTab2]: boolean;
        [fields.showForAuthenticated]: boolean;
        [fields.benefits]: BenefitModel[];
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    defaultBenefitCardIcon: (getSettingsCollection(state).customSettings as any)?.defaultBenefitIcon,
    isAuthenticated: xIsAuthenticated(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface BenefitsWidgetStyles {
    wrapper?: InjectableCss;
    contentAndBenefitsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    heading?: SectionTitleStyles;
    subHeading?: TypographyProps;
    buttonsWrapper?: InjectableCss;
    firstButton?: ButtonPresentationProps;
    secondButton?: ButtonPresentationProps;
    benefitsGridContainer?: GridContainerProps;
    benefitsGridItem?: GridItemProps;
    benefitCard?: ClickablePresentationProps;
    iconTitleWrapper?: InjectableCss;
    description?: TypographyProps;
    cardTitle?: TypographyProps;
    icon?: LazyImageProps;
}

export const benefitsWidgetStyles: BenefitsWidgetStyles = {};

const styles = benefitsWidgetStyles;

interface BenefitModelProps {
    benefitCard: BenefitModel;
    defaultBenefitCardIcon: any;
}

const BenefitCard: FC<BenefitModelProps> = ({ benefitCard, defaultBenefitCardIcon }) => {
    const { url } = useGetLink(benefitCard.fields?.benefitLink);

    return (
        <Clickable href={url} {...styles.benefitCard}>
            <VisuallyHidden as="label">{benefitCard.fields.heading}</VisuallyHidden>
            <StyledWrapper {...styles.iconTitleWrapper}>
                {(benefitCard.fields.icon || defaultBenefitCardIcon) && (
                    <LazyImage
                        width="45"
                        height="45"
                        {...styles.icon}
                        altText={benefitCard.fields.heading}
                        src={benefitCard.fields.icon || defaultBenefitCardIcon}
                        customTransformations={{ default: "GUEST_Product_45x45" }}
                    />
                )}
                {!benefitCard.fields.icon && !defaultBenefitCardIcon && (
                    <StyledWrapper {...styles.icon}></StyledWrapper>
                )}
                {benefitCard.fields.heading && (
                    <Typography {...styles.cardTitle}>{benefitCard.fields.heading}</Typography>
                )}
            </StyledWrapper>
            {benefitCard.fields.subHeading && (
                <Typography {...styles.description}>{parse(benefitCard.fields.subHeading, parserOptions)}</Typography>
            )}
        </Clickable>
    );
};

const BenefitsWidget: React.FC<Props> = ({ fields, defaultBenefitCardIcon, isAuthenticated }) => {
    if (!fields.showForAuthenticated && isAuthenticated) {
        return null;
    }
    const linkOfFirstButton = useGetLink(fields.firstButtonLink);
    const firstButtonTitle = linkOfFirstButton?.title;
    const firstButtonUrl = linkOfFirstButton?.url;

    const linkOfSecondButton = useGetLink(fields.secondButtonLink);
    const secondButtonTitle = linkOfSecondButton?.title;
    const secondButtonUrl = linkOfSecondButton?.url;

    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = (url: string | undefined, openInNewTab: boolean) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            if (openInNewTab) {
                window.open(url, "_blank");
                return;
            }
            window.location.href = url;
        } else {
            if (openInNewTab) {
                window.open(`${window.location.host}/${url}`, "_blank");
                return;
            }

            history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="benefitsWidget">
            <StyledWrapper {...styles.contentAndBenefitsWrapper}>
                <StyledWrapper {...styles.contentWrapper}>
                    {fields.heading &&
                        createWidgetElement("SectionTitle/SectionTitle", {
                            fields: { title: fields.heading, fontFamily: fontFamilyValues.BODONI },
                            extendedStyles: styles.heading,
                        } as any)}
                    {fields.subHeading && (
                        <Typography {...styles.subHeading}>{parse(fields.subHeading, parserOptions)}</Typography>
                    )}
                    {(fields.firstButtonLabel ||
                        linkOfFirstButton ||
                        fields.secondButtonLabel ||
                        linkOfSecondButton) && (
                        <StyledWrapper {...styles.buttonsWrapper}>
                            {(fields.firstButtonLabel || fields.firstButtonLink) && (
                                <Button
                                    {...styles.firstButton}
                                    variant={fields.firstButtonVariant}
                                    onClick={() => onClick(firstButtonUrl, fields.openInNewTab1)}
                                    aria-label={fields.firstButtonLabel || firstButtonTitle || firstButtonUrl}
                                >
                                    {fields.firstButtonLabel || firstButtonTitle || firstButtonUrl}
                                </Button>
                            )}
                            {(fields.secondButtonLabel || fields.secondButtonLink) && (
                                <Button
                                    {...styles.secondButton}
                                    variant={fields.secondButtonVariant}
                                    onClick={() => onClick(secondButtonUrl, fields.openInNewTab2)}
                                    aria-label={fields.secondButtonLabel || secondButtonTitle || secondButtonUrl}
                                >
                                    {fields.secondButtonLabel || secondButtonTitle || secondButtonUrl}
                                </Button>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                {fields.benefits.length > 0 && (
                    <GridContainer {...styles.benefitsGridContainer}>
                        {fields.benefits.map((benefitCard, index) => (
                            <GridItem key={index} {...styles.benefitsGridItem}>
                                <BenefitCard
                                    benefitCard={benefitCard}
                                    defaultBenefitCardIcon={defaultBenefitCardIcon}
                                />
                            </GridItem>
                        ))}
                    </GridContainer>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BenefitsWidget),
    definition: {
        group: customXngageWidget as any,
        fieldDefinitions: [
            {
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Title",
                fieldType: "Translatable",
                defaultValue: "Guest Supply Members",
                isRequired: true,
            },
            {
                name: fields.subHeading,
                editorTemplate: "RichTextField",
                displayName: "Description",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                name: fields.firstButtonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.firstButtonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.firstButtonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                // XNG-CHANGES : GUEST-2657 : Removing tertiary option
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
                isRequired: false,
                defaultValue: "primary",
                fieldType: "General",
                hideEmptyOption: true,
            },
            {
                name: fields.openInNewTab1,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.secondButtonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.secondButtonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.secondButtonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                // XNG-CHANGES : GUEST-2657 : Removing tertiary option
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
                isRequired: false,
                defaultValue: "primary",
                fieldType: "General",
                hideEmptyOption: true,
            },
            {
                name: fields.openInNewTab2,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showForAuthenticated,
                displayName: "Show for Authenticated",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.benefits,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { heading } }) => heading || "Benefit Card",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "heading",
                        displayName: "Benefit Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "subHeading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        isRequired: true,
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "icon",
                        displayName: "Icon Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "benefitLink",
                        displayName: "Benefit card link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
